/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictProvider = require('pict-provider');
const libTestResultOptions = require('./_TestResultOptions.js');

const DATA_REQUIREMENTS_SOLVER_SOURCE = 'DataRequirements';

// The host form's own inputs carry no OriginalHash, so they translate to themselves.
const HOST_MANIFEST_COPY = Object.freeze({ RegistrationHash: null, TestHash: null, HashTranslation: Object.freeze({ }), AddressTranslation: Object.freeze({ }) });

class DataRequirementsProvider extends PictProvider
{
	/**
	 * @param {Pict} pFable - The Pict instance.
	 * @param {Record<string, any>} [pOptions] - (optional) The options for the provider.
	 * @param {string} [pServiceHash] - (optional) The service hash for the provider.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict & { ExpressionParser: any }} */
		this.pict;

		/** @type {import('fable') & import('pict') & { ExpressionParser: any }} */
		this.fable;

		/** @type {Record<string, any>} */
		this.options;

		this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));

		if (!this.options.hasOwnProperty('HeadlightAPIURL'))
		{
			this.options.HeadlightAPIURL = '/1.0/';
		}
		this.fable.serviceManager.instantiateServiceProviderIfNotExists('HeadlightRestClient', { ServerURL: this.options.HeadlightAPIURL });
		this.pict.instantiateServiceProviderIfNotExists('ExpressionParser');
		this.pict.ExpressionParser.addSolverFunction('settestresult', 'fable.providers.DataRequirementsProvider._setTestResult', 'Sets a test result by hash (for test requirement outcomes)', [ 2, 4 ]);
		this.pict.ExpressionParser.addSolverFunction('testresultlabel', 'fable.providers.DataRequirementsProvider._getTestResultLabel', 'The display text for a stored test-result code');
	}

	/**
	 * @param {string} pCode - The stored test-result code.
	 * @return {string} - Its display text, or the value itself when it isn't a known code.
	 */
	_getTestResultLabel(pCode)
	{
		return libTestResultOptions.getTestResultLabel(pCode);
	}

	/**
	 * @param {string} pTestHash - The hash of the test to set the result for.
	 * @param {string} pTestResult - The test result hash to set for the test.
	 * @param {string} [pPreviousValueAddress] - (optional) If provided, will only apply the change if the current value differes from the previous value (if present).
	 * @param {string} [pCurrentValue] - (optional) Should be provided if pPreviousValueAddress is provided. Value that will be stored if the change is applied.
	 * @param {string} [pPreviousValueFallbackAddress] - (optional) Read when pPreviousValueAddress holds nothing. Documents saved before the last-value key gained a copy scope hold it at the unscoped address.
	 */
	_setTestResult(pTestHash, pTestResult, pPreviousValueAddress, pCurrentValue, pPreviousValueFallbackAddress)
	{
		const tmpPayloadUUID = this.pict.getUUID();
		if (pPreviousValueAddress)
		{
			if (pCurrentValue != null && this._getPreviousTestResultValue(pPreviousValueAddress, pPreviousValueFallbackAddress) === pCurrentValue)
			{
				return;
			}
		}
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`SetTestResultByHash:${pTestHash}:${pTestResult}:TempData["${tmpPayloadUUID}"]`);
		if (this.pict.TempData[tmpPayloadUUID])
		{
			// we only set the previous value if the change was applied; ex. in cases where the solver runs before an injected test has been resolved
			if (pPreviousValueAddress)
			{
				this.pict.manifest.setValueByHash(this.pict, pPreviousValueAddress, pCurrentValue);
			}
			delete this.pict.TempData[tmpPayloadUUID];
		}
	}

	/**
	 * The last value a test result outcome was applied for, read from the copy-scoped address and falling back
	 * to the unscoped one a document saved before the scope existed holds. Nothing is written back through the
	 * fallback: an old document keeps the key it was saved with until its outcome actually changes.
	 *
	 * @param {string} pPreviousValueAddress - The address the current solver writes.
	 * @param {string} [pPreviousValueFallbackAddress] - The pre-copy-scope address, when it differs.
	 *
	 * @return {any}
	 */
	_getPreviousTestResultValue(pPreviousValueAddress, pPreviousValueFallbackAddress)
	{
		const tmpValue = this.pict.manifest.getValueByHash(this.pict, pPreviousValueAddress);
		if (tmpValue == null && pPreviousValueFallbackAddress)
		{
			return this.pict.manifest.getValueByHash(this.pict, pPreviousValueFallbackAddress);
		}
		return tmpValue;
	}

	/**
	 * @param {(pError?: Error) => void} fCallback - The callback to call after data load is complete.
	 *
	 * @return {void}
	 */
	onAfterLoadDataAsync(fCallback)
	{
		return super.onAfterLoadDataAsync((pError) =>
		{
			if (pError)
			{
				return fCallback(pError);
			}

			const tmpManifest = this.pict.settings.MyDefaultFormManifest;
			if (!tmpManifest)
			{
				return fCallback();
			}
			const tmpMarshalDestination = this.pict.providers.DataBroker.resolveMarshalDestinationObject(this.pict.views.PictFormMetacontroller.viewMarshalDestination);
			this._populateDataRequiremetsForManifest(tmpManifest, tmpMarshalDestination, fCallback);
		});
	}

	/**
	 * Check to see if any new tests need to have data requirements loaded.
	 *
	 * @param {Record<string, any>} pManifest
	 * @param {(pError?: Error) => void} fCallback
	 * @param {Array<string>} [pTestHashes] - (optional) If provided, will include these test hashes when determining data requirements, in addition to any hashes already present in the manifest or marshal destination.
	 */
	updateDataRequirementsForManifest(pManifest, fCallback, pTestHashes)
	{
		if (Array.isArray(pTestHashes) && pTestHashes.length > 0)
		{
			if (this.AppData._DataRequirementsCache && Array.isArray(this.AppData._DataRequirementsCache.Tests))
			{
				if (pTestHashes.every((h) => this.AppData._DataRequirementsCache.Tests.some((t) => t.Hash === h)))
				{
					return fCallback();
				}
			}
		}
		this._populateDataRequiremetsForManifest(pManifest, this.pict.providers.DataBroker.resolveMarshalDestinationObject(this.pict.views.PictFormMetacontroller.viewMarshalDestination), fCallback);
	}

	/**
	 * @param {Record<string, any>} pManifest
	 * @param {Record<string, any>} pMarshalDestination
	 * @param {(pError?: Error) => void} fCallback
	 * @param {Array<string>} [pTestHashes] - (optional) If provided, will include these test hashes.
	 */
	_populateDataRequiremetsForManifest(pManifest, pMarshalDestination, fCallback, pTestHashes)
	{
		const tmpTestHashes = new Set(pTestHashes || []);
		for (const tmpDescriptor of Object.values(pManifest.Descriptors || {}))
		{
			if (tmpDescriptor.PictForm?.TestHashes)
			{
				if (Array.isArray(tmpDescriptor.PictForm.TestHashes))
				{
					for (const tmpHash of tmpDescriptor.PictForm.TestHashes)
					{
						tmpTestHashes.add(tmpHash);
					}
				}
				else
				{
					tmpTestHashes.add(tmpDescriptor.PictForm.TestHashes);
				}
			}
			else if (tmpDescriptor.PictForm && (tmpDescriptor.PictForm.InputType === 'MaterialsTestSelector' || tmpDescriptor.PictForm.InputType === 'MaterialsHiddenTestSelector'))
			{
				if (tmpDescriptor.Default)
				{
					tmpTestHashes.add(tmpDescriptor.Default);
				}
			}
		}
		if (pMarshalDestination && pMarshalDestination.__DynamicSectionData)
		{
			for (const tmpHash of pMarshalDestination.__DynamicSectionData.Sequence)
			{
				const tmpSectionData = pMarshalDestination.__DynamicSectionData[tmpHash];
				if (tmpSectionData && tmpSectionData.TestHash)
				{
					tmpTestHashes.add(tmpSectionData.TestHash);
				}
			}
		}
		const tmpPromises = [];
		if (tmpTestHashes.size > 0)
		{
			tmpPromises.push(this.loadDataRequirementsForTestHashes(Array.from(tmpTestHashes)).catch((pError) =>
			{
				this.log.error('Error loading data requirements for test hashes', { testHashes: Array.from(tmpTestHashes), error: pError });
			}));
		}
		Promise.all(tmpPromises).finally(() => fCallback());
	}

	/**
	 * @param {Array<string>} pTestHashes
	 *
	 * @return {Promise<void>}
	 */
	async loadDataRequirementsForTestHashes(pTestHashes)
	{
		try
		{
			if (pTestHashes.length < 1)
			{
				this.log.info('No test hashes provided, skipping data requirements load.', { testHashes: pTestHashes });
				return;
			}
			const tmpTests = await this._get(`Tests/FilteredTo/FBL~Hash~INN~${pTestHashes.map((h) => encodeURIComponent(h)).join(',')}`);
			if (tmpTests.length < 1)
			{
				this.log.info('No tests found for provided test hashes, unable to determine data requirements.', { testHashes: pTestHashes });
				return;
			}
			const tmpTestSpecificationMaterialTestJoins = await this._getAll(`TestSpecificationMaterialTestJoins/FilteredTo/FBL~IDTest~INN~${tmpTests.map((t) => t.IDTest).join(',')}`);
			const tmpTestSpecificationIDs = Array.from(new Set(tmpTestSpecificationMaterialTestJoins.map((j) => j.IDTestSpecification)));
			const tmpHasSpecifications = tmpTestSpecificationIDs.length > 0;
			const tmpTestSpecificationRequirements = tmpHasSpecifications ? await this._getAll(`TestSpecificationRequirements/FilteredTo/FBL~IDTestSpecification~INN~${tmpTestSpecificationIDs.join(',')}`) : [ ];
			const tmpTestSpecifications = tmpHasSpecifications ? await this._getAll(`TestSpecifications/FilteredTo/FBL~IDTestSpecification~INN~${tmpTestSpecificationIDs.join(',')}`) : [ ];
			const tmpTestSpecificationToSetIDMap = {};
			for (const tmpTestSpecification of tmpTestSpecifications)
			{
				tmpTestSpecificationToSetIDMap[tmpTestSpecification.IDTestSpecification] = tmpTestSpecification.IDTestSpecificationSet;
			}
			this.AppData._DataRequirementsCache =
			{
				Tests: tmpTests ?? [],
				TestSpecificationMaterialTestJoins: tmpTestSpecificationMaterialTestJoins ?? [],
				TestSpecificationRequirements: tmpTestSpecificationRequirements ?? [],
				TestSpecifications: tmpTestSpecifications ?? [],
				TestSpecificationToSetIDMap: tmpTestSpecificationToSetIDMap ?? [],
			};
			if (this.AppData._DataRequirementsDeferredLoad)
			{
				const tmpParams = this.AppData._DataRequirementsDeferredLoad;
				delete this.AppData._DataRequirementsDeferredLoad;
				await this.applyDataRequirements(tmpParams.Manifest, tmpParams.SampleRecord);
			}
		}
		catch (pError)
		{
			this.log.error('Failed to load data requirements for test hashes', { testHashes: pTestHashes, error: pError });
		}
	}

	/**
	 * @param {Record<string, any>} pManifest
	 * @param {Record<string, any>} pSampleRecord
	 */
	async applyDataRequirements(pManifest, pSampleRecord)
	{
		if (!pSampleRecord)
		{
			this.log.warn('No valid sample record provided, unable to determine data requirements.', { sampleRecord: pSampleRecord });
			return;
		}
		if (!this.AppData._DataRequirementsCache || !Array.isArray(this.AppData._DataRequirementsCache.TestSpecificationMaterialTestJoins) ||
			!Array.isArray(this.AppData._DataRequirementsCache.TestSpecificationRequirements) || this.AppData._DataRequirementsCache.TestSpecificationRequirements.length < 1)
		{
			//FIXME: defer in case cache is still loading?
			this.AppData._DataRequirementsDeferredLoad = { Manifest: pManifest, SampleRecord: pSampleRecord };
			return;
		}
		const tmpSample = pSampleRecord;
		let tmpTestSpecificationSet;
		if (tmpSample)
		{
			if (tmpSample.IDProject > 0)
			{
				const tmpProject = await this._get(`Projects/Lite/FilteredTo/FBV~IDProject~EQ~${tmpSample.IDProject}`).then((projects) => projects[0]);
				if (tmpProject)
				{
					const tmpContract = await this._get(`Contract/${tmpProject.IDContract}`);
					if (tmpContract)
					{
						if (tmpContract.ItemIDTestSpecificationSet > 0)
						{
							tmpTestSpecificationSet = await this._get(`TestSpecificationSets/FilteredTo/FBV~IDTestSpecificationSet~EQ~${tmpContract.ItemIDTestSpecificationSet}`).then((sets) => sets[0]);
						}
						if (!tmpTestSpecificationSet && tmpContract.ItemSpecificationYear)
						{
							// A year is NOT unique (a provisional set can share it with the live one) and nothing on
							// the record distinguishes them — take the lowest id and flag the ambiguity.
							tmpTestSpecificationSet = await this._get(`TestSpecificationSets/FilteredTo/FBV~Year~EQ~${tmpContract.ItemSpecificationYear}~FSF~IDTestSpecificationSet~ASC~0`).then((sets) =>
							{
								if (Array.isArray(sets) && sets.length > 1)
								{
									this.log.warn(`Specification year ${tmpContract.ItemSpecificationYear} matches ${sets.length} specification sets; using the lowest id (${sets[0]?.IDTestSpecificationSet}). Set the contract's ItemIDTestSpecificationSet to resolve this explicitly.`);
								}
								return Array.isArray(sets) ? sets[0] : undefined;
							});
						}
					}
				}
			}
			if (!tmpTestSpecificationSet)
			{
				//FIXME: default specification set logic?
				this.log.info('Sample is not linked to a project, unable to determine specification set based on project contract. Defaulting to most recent specification set.', { sampleRecord: pSampleRecord });
				tmpTestSpecificationSet = await this._get(`TestSpecificationSets/FilteredTo/FSF~Year~DESC~0/0/1`).then((sets) => sets[0]);
			}
			if (!tmpTestSpecificationSet)
			{
				this.log.warn('No test specification sets found, unable to determine test specification requirements.', { sampleRecord: pSampleRecord });
				return;
			}
			const tmpSampleIDMaterial = tmpSample.IDMaterial ?? 0;
			const tmpValidSpecs = new Set();
			for (const tmpSpec of this.AppData._DataRequirementsCache.TestSpecifications)
			{
				if (tmpSpec.IDTestSpecificationSet == tmpTestSpecificationSet.IDTestSpecificationSet)
				{
					tmpValidSpecs.add(tmpSpec.IDTestSpecification);
				}
			}
			const tmpSpecificationMaterialJoins = this.AppData._DataRequirementsCache.TestSpecificationMaterialTestJoins
				.filter((j) => j.IDMaterial == tmpSampleIDMaterial && tmpValidSpecs.has(j.IDTestSpecification));
			const tmpSpecificationIDs = new Set(tmpSpecificationMaterialJoins.map((j) => j.IDTestSpecification));
			const tmpTestSpecificationRequirements = this.AppData._DataRequirementsCache.TestSpecificationRequirements
				.filter((r) => tmpSpecificationIDs.has(r.IDTestSpecification));
			if (tmpTestSpecificationRequirements.length < 1)
			{
				return;
			}

			// convert requirements into solver statements, once per copy of the test the requirement targets
			const tmpHostTestHashes = this._collectHostTestHashes(pManifest);
			const tmpSolvers = [];
			for (const tmpRequirement of tmpTestSpecificationRequirements)
			{
				const tmpTestID = tmpSpecificationMaterialJoins.find((j) => j.IDTestSpecification === tmpRequirement.IDTestSpecification)?.IDTest;
				const tmpTest = this.AppData._DataRequirementsCache.Tests.find((t) => t.IDTest === tmpTestID);
				const tmpCopies = this._resolveRequirementCopies(tmpTest?.Hash, tmpHostTestHashes);
				if (tmpCopies.length < 1)
				{
					this.log.info(`Test specification requirement ${tmpRequirement.IDTestSpecificationRequirement} targets test ${tmpTest?.Hash ?? tmpTestID}, which is not on this form; skipping it.`,
						{ IDTestSpecificationRequirement: tmpRequirement.IDTestSpecificationRequirement, IDTest: tmpTestID, TestHash: tmpTest?.Hash });
					continue;
				}
				for (const tmpCopy of tmpCopies)
				{
					tmpSolvers.push(...this._buildRequirementSolvers(pManifest, tmpRequirement, tmpTest, tmpTestID, tmpCopy, tmpCopies.length));
				}
			}
			if (tmpSolvers.length > 0)
			{
				this._replaceGeneratedValidationSolvers(pManifest, tmpSolvers);
				if (this.pict.PictApplication)
				{
					this.pict.PictApplication.solve();
				}
			}
		}
	}

	/**
	 * The test hashes the host form declares on its own inputs. A descriptor carrying an OriginalHash or an
	 * OriginalDataAddress came from an injected copy, which the section manager tracks separately; what is
	 * left is the host's own, and its references translate to themselves.
	 *
	 * @param {Record<string, any>} pManifest - The merged form manifest.
	 *
	 * @return {Set<string>}
	 */
	_collectHostTestHashes(pManifest)
	{
		const tmpTestHashes = new Set();
		for (const tmpDescriptor of Object.values(pManifest?.Descriptors || { }))
		{
			if (tmpDescriptor.OriginalHash || tmpDescriptor.OriginalDataAddress)
			{
				continue;
			}
			if (tmpDescriptor.PictForm?.TestHashes)
			{
				const tmpDescriptorHashes = Array.isArray(tmpDescriptor.PictForm.TestHashes) ? tmpDescriptor.PictForm.TestHashes : [ tmpDescriptor.PictForm.TestHashes ];
				for (const tmpHash of tmpDescriptorHashes)
				{
					tmpTestHashes.add(tmpHash);
				}
			}
			else if (tmpDescriptor.PictForm && (tmpDescriptor.PictForm.InputType === 'MaterialsTestSelector' || tmpDescriptor.PictForm.InputType === 'MaterialsHiddenTestSelector'))
			{
				if (tmpDescriptor.Default)
				{
					tmpTestHashes.add(tmpDescriptor.Default);
				}
			}
		}
		return tmpTestHashes;
	}

	/**
	 * Which copies of a test's inputs a requirement should be solved against.
	 *
	 * A test can be injected more than once, and every injected copy carries the same OriginalHash values, so
	 * the translation has to come from the copy the section manager recorded at injection rather than from the
	 * merged form manifest. A test the host form owns needs no translation at all.
	 *
	 * @param {string} [pTestHash] - The Test.Hash the requirement resolved to.
	 * @param {Set<string>} pHostTestHashes - The test hashes the host form declares on its own inputs.
	 *
	 * @return {Array<Record<string, any>>} - One entry per copy; empty when the test is not on this form.
	 */
	_resolveRequirementCopies(pTestHash, pHostTestHashes)
	{
		const tmpSectionManager = this.pict.providers?.DocumentDynamicSectionManager;
		if (pTestHash && tmpSectionManager && typeof tmpSectionManager.getInjectedManifestTranslationsForTestHash === 'function')
		{
			const tmpInjectedCopies = tmpSectionManager.getInjectedManifestTranslationsForTestHash(pTestHash);
			if (tmpInjectedCopies.length > 0)
			{
				return tmpInjectedCopies;
			}
		}
		if (!pTestHash || !tmpSectionManager || pHostTestHashes.has(pTestHash))
		{
			return [ HOST_MANIFEST_COPY ];
		}
		return [];
	}

	/**
	 * The descriptor an outcome target names, within one copy of the manifest.
	 *
	 * @param {Record<string, any>} pManifest - The merged form manifest.
	 * @param {string} pInputHash - The input hash as the requirement names it, before any salting.
	 * @param {Record<string, any>} pCopy - The copy to resolve within.
	 *
	 * @return {Record<string, any>|undefined}
	 */
	_findCopyDescriptor(pManifest, pInputHash, pCopy)
	{
		const tmpCopyHash = pCopy.RegistrationHash ? pCopy.HashTranslation[pInputHash] : pInputHash;
		if (!tmpCopyHash)
		{
			return undefined;
		}
		for (const tmpDescriptor of Object.values(pManifest?.Descriptors || { }))
		{
			if (tmpDescriptor.Hash !== tmpCopyHash || !tmpDescriptor.PictForm || !tmpDescriptor.PictForm.Section)
			{
				continue;
			}
			if (!pCopy.RegistrationHash && tmpDescriptor.OriginalHash)
			{
				continue;
			}
			return tmpDescriptor;
		}
		return undefined;
	}

	/**
	 * Build the validation solvers for one requirement against one copy of the test's inputs, rewritten
	 * through that copy's own translation maps.
	 *
	 * @param {Record<string, any>} pManifest - The merged form manifest.
	 * @param {Record<string, any>} pRequirement - The TestSpecificationRequirement row.
	 * @param {Record<string, any>} [pTest] - The Test row the requirement resolved to.
	 * @param {number} [pTestID] - The IDTest the requirement resolved to, for diagnostics when pTest is missing.
	 * @param {Record<string, any>} pCopy - The copy to solve against.
	 * @param {number} pCopyCount - How many copies of this test are on the form.
	 *
	 * @return {Array<Record<string, any>>}
	 */
	_buildRequirementSolvers(pManifest, pRequirement, pTest, pTestID, pCopy, pCopyCount)
	{
		const tmpSolvers = [];
		const tmpLegacyResultKey = `Result_${pRequirement.IDTestSpecificationRequirement}`;
		// The last-value address is written into FormData and persisted with the document, so the key only
		// gains a copy scope where it has to: when the same test is on the form more than once. A form with a
		// single copy keeps the key every document saved before this change already holds.
		const tmpResultKey = (pCopyCount > 1 && pCopy.RegistrationHash) ? `${tmpLegacyResultKey}_${pCopy.RegistrationHash}` : tmpLegacyResultKey;
		const tmpResultAddress = `TempData._DataRequirementsValues.${tmpResultKey}`;
		const tmpLastValueAddress = `AppData.DocumentData.ReportData.FormData._DataRequirementsLastValues.${tmpResultKey}`;
		const tmpLegacyLastValueAddress = `AppData.DocumentData.ReportData.FormData._DataRequirementsLastValues.${tmpLegacyResultKey}`;
		const tmpComputationTemplate = pRequirement.ComputationTemplate;
		switch (pRequirement.OperationType)
		{
		case 'AddressInRange':
			// get value at address in ComputationTemplate
			tmpSolvers.push(`SETVALUE("${tmpResultAddress}", IF(${tmpComputationTemplate}, ">=", "${pRequirement.ValueMinimum}", IF(${tmpComputationTemplate}, "<=", "${pRequirement.ValueMaximum}", 1, 0), 0))`);
			break;
		case 'AddressOutOfRange':
			tmpSolvers.push(`SETVALUE("${tmpResultAddress}", IF(${tmpComputationTemplate}, ">=", "${pRequirement.ValueMaximum}", 1, IF(${tmpComputationTemplate}, "<=", "${pRequirement.ValueMinimum}", 1, 0)))`);
			break;
		case 'Solver': // just run it; outcome doesn't matter
			tmpSolvers.push(tmpComputationTemplate);
			return this._translateCopySolvers(tmpSolvers, pCopy);
		case 'TrueExpression':
			tmpSolvers.push(`SETVALUE("${tmpResultAddress}", IF(${tmpComputationTemplate}, "==", "true", 1, IF(${tmpComputationTemplate}, "==", 1, 1, IF(${tmpComputationTemplate}, "==", "yes", 1, 0))))`);
			break;
		case 'FalseExpression':
			tmpSolvers.push(`SETVALUE("${tmpResultAddress}", IF(${tmpComputationTemplate}, "==", "true", 0, IF(${tmpComputationTemplate}, "==", 1, 0, IF(${tmpComputationTemplate}, "==", "yes", 0, 1))))`);
			break;
		}
		const tmpOutcomeTargets = typeof pRequirement.OutcomeTargets !== 'string' ? [] : pRequirement.OutcomeTargets.split(',').map((t) => t.trim()).filter((t) => t.length > 0);
		switch (pRequirement.OutcomeType)
		{
		case 'ColorInputs':
			for (const tmpInputHash of tmpOutcomeTargets)
			{
				const tmpDescriptor = this._findCopyDescriptor(pManifest, tmpInputHash, pCopy);
				if (!tmpDescriptor)
				{
					continue;
				}
				//TODO: is there a risk of having quoted values in the DB?
				const tmpOutcome = `IF(GETVALUE("${tmpResultAddress}"), "==", 1, "${pRequirement.ComputationSuccessOutcome}", "${pRequirement.ComputationFailureOutcome}")`;
				tmpSolvers.push(`colorinputbackground("${tmpDescriptor.PictForm.Section}", "${tmpInputHash}", ${tmpOutcome}, WHEN(${tmpOutcome}, 1, 0))`);
			}
			break;
		case 'ShowSection':
			for (const tmpSectionHash of tmpOutcomeTargets)
			{
				tmpSolvers.push(`setsectionvisibility("${tmpSectionHash}", GETVALUE("${tmpResultAddress}"))`);
			}
			break;
		case 'HideSection':
			for (const tmpSectionHash of tmpOutcomeTargets)
			{
				tmpSolvers.push(`setsectionvisibility("${tmpSectionHash}", IF(GETVALUE("${tmpResultAddress}"), "==", 1, 0, 1))`);
			}
			break;
		case 'AssignContent':
			// do we have a solver function for this? do not think so... just doing an assingment for now
			for (const tmpOutputHash of tmpOutcomeTargets)
			{
				tmpSolvers.push(`${tmpOutputHash} = IF(GETVALUE("${tmpResultAddress}"), ${pRequirement.ComputationSuccessOutcome}, ${pRequirement.ComputationFailureOutcome}))`);
			}
			break;
		case 'TestResult':
			{
				//FIXME: should these somehow use tracking GUIDs? this still falls over when the same test is on
				//       the form twice: the solver names the test by hash, so the result lands on whichever
				//       TestInstance the global event resolves first.
				// The fifth argument is the pre-copy-scope address, so a document saved before the key gained a
				// scope is still read rather than re-applied from scratch.
				const tmpLastValueArguments = (tmpResultKey === tmpLegacyResultKey) ? `"${tmpLastValueAddress}", GETVALUE("${tmpResultAddress}")` : `"${tmpLastValueAddress}", GETVALUE("${tmpResultAddress}"), "${tmpLegacyLastValueAddress}"`;
				tmpSolvers.push(`settestresult("${pTest?.Hash ?? `test ${pTestID} not found`}", IF(GETVALUE("${tmpResultAddress}"), "==", 1, "${pRequirement.ComputationSuccessOutcome}", "${pRequirement.ComputationFailureOutcome}"), ${tmpLastValueArguments})`);
			}
			break;
		}
		return this._translateCopySolvers(tmpSolvers, pCopy);
	}

	/**
	 * Rewrite raw solver expressions through one copy's translation maps and wrap them as generated solvers.
	 *
	 * @param {Array<string>} pSolverExpressions - The raw expressions.
	 * @param {Record<string, any>} pCopy - The copy whose maps to rewrite through.
	 *
	 * @return {Array<Record<string, any>>}
	 */
	_translateCopySolvers(pSolverExpressions, pCopy)
	{
		return pSolverExpressions.map((pExpression) => (
			{
				Expression: this.pict.views.PictFormMetacontroller.rewriteSolverExpression(pExpression, pCopy.HashTranslation, pCopy.AddressTranslation),
				Ordinal: 1,
				SolverSource: DATA_REQUIREMENTS_SOLVER_SOURCE,
			}));
	}

	/**
	 * Is this validation solver one this provider generated from test specification requirements?
	 *
	 * @param {any} pSolver - A validation solver entry, either an expression string or a solver object.
	 *
	 * @return {boolean}
	 */
	_isGeneratedValidationSolver(pSolver)
	{
		return (pSolver != null) && (typeof pSolver === 'object') && (pSolver.SolverSource === DATA_REQUIREMENTS_SOLVER_SOURCE);
	}

	/**
	 * Swap the validation solvers this provider generated on a previous pass for a freshly generated set,
	 * in place, leaving every other entry alone.
	 *
	 * The form manifest's ValidationSolvers array is shared: the metacontroller merges each injected
	 * section copy's translated solvers into it, so this provider may only touch its own entries.
	 *
	 * @param {Record<string, any>} pManifest - The form manifest holding the validation solvers.
	 * @param {Array<Record<string, any>>} pGeneratedSolvers - The generated solvers to write.
	 *
	 * @return {void}
	 */
	_replaceGeneratedValidationSolvers(pManifest, pGeneratedSolvers)
	{
		const tmpExistingSolvers = Array.isArray(pManifest.ValidationSolvers) ? pManifest.ValidationSolvers : [];
		let tmpInsertAtIndex = tmpExistingSolvers.findIndex((pSolver) => this._isGeneratedValidationSolver(pSolver));
		const tmpRetainedSolvers = tmpExistingSolvers.filter((pSolver) => !this._isGeneratedValidationSolver(pSolver));
		if (tmpInsertAtIndex < 0)
		{
			tmpInsertAtIndex = tmpRetainedSolvers.length;
		}
		tmpRetainedSolvers.splice(tmpInsertAtIndex, 0, ...pGeneratedSolvers);
		pManifest.ValidationSolvers = tmpRetainedSolvers;
	}

	/**
	 * @param {any} pValue
	 *
	 * @return {boolean}
	 */
	_isTruthy(pValue)
	{
		if (pValue == null)
		{
			return false;
		}
		if (typeof pValue === 'boolean')
		{
			return pValue;
		}
		if (typeof pValue === 'number')
		{
			return pValue != 0;
		}
		const tmpCleanValue = String(pValue).trim().toLowerCase();
		return tmpCleanValue == '1' || tmpCleanValue == 'true' || tmpCleanValue == 'yes' || tmpCleanValue == 'y' || tmpCleanValue == 't';
	}

	/**
	 * Perform a JSON GET request.
	 *
	 * @param {string} uri - The URI to get.
	 *
	 * @return {Promise<any>} - The response.
	 */
	async _get(uri)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(uri, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * @param {string} uri - The URI to get all results from, should include any necessary filtering but not pagination (skip/take).
	 *
	 * @return {Promise<Array<any>>} - The aggregated results from all pages.
	 */
	async _getAll(uri)
	{
		let tmpResults = [];
		let tmpSkip = 0;
		const tmpPageSize = 250;
		while (true)
		{
			const tmpPage = await this._get(`${uri}/${tmpSkip}/${tmpPageSize}`);
			tmpResults = tmpResults.concat(tmpPage);
			if (tmpPage.length < tmpPageSize)
			{
				break;
			}
			tmpSkip += tmpPageSize;
		}
		return tmpResults;
	}
}

module.exports = DataRequirementsProvider;
module.exports.default_configuration =
{
	// register to receive data load/save lifecycle events, which are off by default
	AutoSaveDataWithApp: true,
	// default ordinal is zero; we want to come after other providers (ex. the dynamic section manager) so any existing dynamic sections are already loaded when we run
	AutoLoadDataOrdinal: 1,
};
